/**
 * The samagri catalogue — items and kits (content 07 §3, CSR-009), over `/v1/admin/samagri`.
 *
 * An item is one thing a puja uses (a coconut, a kalash, a packet of roli) with its unit and its
 * cost as of a date. A kit is a named list of items with quantities, usually for one puja and one
 * regional tradition.
 *
 * ⛔ **A kit's item count and estimated cost are the server's.** They are derived from its lines on
 * every change and never accepted from a client, so this screen shows what came back and sends
 * only the lines. Editing an item's cost moves every kit that holds it, which is why each write
 * refreshes both lists.
 *
 * ⛔ **No delete.** Kits and provisions cite items, and bookings cite kits, so both are switched off
 * instead. Every refusal is the server's, shown verbatim.
 */
import { useState, type ReactNode } from 'react';
import { Card, Icon, StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable } from '../components/data';
import { Consequences, FilterBar, Money, ScopeAction, REFUSED } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { formatInr, parsePaise } from '../lib/money';
import {
  useCreateSamagriItem,
  useCreateSamagriKit,
  useReplaceKitLines,
  useSamagriItems,
  useSamagriKit,
  useSamagriKits,
  useUpdateSamagriItem,
  useUpdateSamagriKit,
  type SamagriCategory,
  type SamagriItemKind,
  type SamagriItemRow,
  type SamagriKitDetail,
  type SamagriKitRow,
  type SamagriRegion,
} from '../lib/api/hooks/samagri';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';

export const CATEGORIES: readonly SamagriCategory[] = [
  'auspicious_powder',
  'grain_pulse',
  'flower_leaf',
  'fruit_sweet',
  'dairy_ghee',
  'wood_fuel',
  'fire_offering',
  'thread_cloth',
  'vessel_equipment',
  'light_incense',
  'water_liquid',
];

export const KINDS: readonly SamagriItemKind[] = ['consumable', 'equipment', 'perishable_fresh', 'printed'];

export const REGIONS: readonly SamagriRegion[] = [
  'north',
  'south',
  'east',
  'west',
  'maharashtra',
  'bengal',
  'tamil',
  'telugu',
  'kannada',
  'malayalam',
  'gujarati',
  'marathi',
];

/** `vessel_equipment` → "Vessel equipment": the code as an operator reads it. */
export function words(code: string): string {
  const text = code.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Paise as the rupee text a cost field starts from: 2500 → "25.00", null → "". */
export function rupeesText(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '';
  return `${Math.floor(paise / 100)}.${String(paise % 100).padStart(2, '0')}`;
}

const QUANTITY = /^\d{1,6}(\.\d{1,2})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const WHOLE = /^\d{1,5}$/;

/** A field's error, or `undefined` when it is blank or well-formed. */
function check(value: string, ok: (v: string) => boolean, message: string): string | undefined {
  return value.trim() === '' || ok(value.trim()) ? undefined : message;
}

const blank = (value: string) => value.trim() || null;

function Switch({ label, on, onChange }: { label: string; on: boolean; onChange: (next: boolean) => void }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', fontSize: 'var(--fs-admin-ui)' }}>
      <Toggle label={label} on={on} onChange={onChange} />
      {label}
    </span>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>{children}</div>;
}

function ItemForm({ row, onDone }: { row: SamagriItemRow | null; onDone: () => void }) {
  const { scope, toast } = useConsole();
  const create = useCreateSamagriItem();
  const update = useUpdateSamagriItem();
  const mutation = row ? update : create;
  const [slug, setSlug] = useState('');
  const [nameEn, setNameEn] = useState(row?.name_en ?? '');
  const [nameHi, setNameHi] = useState(row?.name_hi ?? '');
  const [nameSa, setNameSa] = useState(row?.name_sa ?? '');
  const [category, setCategory] = useState<SamagriCategory>((row?.category as SamagriCategory) ?? 'auspicious_powder');
  const [kind, setKind] = useState<SamagriItemKind>((row?.item_kind as SamagriItemKind) ?? 'consumable');
  const [unit, setUnit] = useState(row?.unit ?? '');
  const [quantity, setQuantity] = useState(row?.default_quantity ?? '');
  const [cost, setCost] = useState(rupeesText(row?.unit_cost_paise));
  const [costAsOf, setCostAsOf] = useState(row?.cost_as_of ?? '');
  const [perishable, setPerishable] = useState(row?.is_perishable ?? false);
  const [shelfLife, setShelfLife] = useState(row?.shelf_life_days ? String(row.shelf_life_days) : '');
  const [local, setLocal] = useState(row?.is_locally_sourced ?? false);
  const [description, setDescription] = useState(row?.description ?? '');
  const [hsn, setHsn] = useState(row?.hsn_code ?? '');

  const errors = {
    quantity: check(quantity, (v) => QUANTITY.test(v), 'Up to two decimal places, like 1.5.'),
    cost: check(cost, (v) => (parsePaise(v) ?? -1) >= 0, 'Rupees, like 25 or 25.50.'),
    costAsOf: check(costAsOf, (v) => DATE.test(v), 'A date as YYYY-MM-DD.'),
    shelfLife: check(shelfLife, (v) => WHOLE.test(v) && Number(v) >= 1, 'Whole days, 1 or more.'),
  };
  const invalid = Object.values(errors).some(Boolean);
  const fields = {
    name_en: nameEn.trim(),
    name_hi: nameHi.trim(),
    name_sa: blank(nameSa),
    category,
    item_kind: kind,
    unit: unit.trim(),
    default_quantity: blank(quantity),
    unit_cost_paise: parsePaise(cost),
    cost_as_of: blank(costAsOf),
    is_perishable: perishable,
    shelf_life_days: shelfLife.trim() ? Number(shelfLife) : null,
    is_locally_sourced: local,
    description: blank(description),
    hsn_code: blank(hsn),
  };
  const done = (message: string, tone: 'success' | 'info' | 'error' = 'success') => {
    toast({ tone, message });
    onDone();
  };
  const save = () =>
    row
      ? update.mutate({ id: row.id, patch: fields }, { onSuccess: () => done(`${row.slug} saved`) })
      : create.mutate({ ...fields, slug: slug.trim() }, { onSuccess: () => done(`${slug.trim()} added`) });
  const toggle = (target: SamagriItemRow) =>
    update.mutate(
      { id: target.id, patch: { is_active: !target.is_active } },
      {
        onSuccess: () =>
          done(`${target.slug} ${target.is_active ? 'switched off' : 'switched on'}`, target.is_active ? 'info' : 'success'),
      },
    );

  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        <strong>{row ? row.slug : 'New samagri item'}</strong>
        {row ? (
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
            The slug is fixed once created — kits and provisions cite it.
          </span>
        ) : (
          <Input density="admin" label="Slug" value={slug} onChange={setSlug} placeholder="roli-kumkum" />
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--sp-md)' }}>
          <Input density="admin" label="Name (English)" value={nameEn} onChange={setNameEn} />
          <Input density="admin" label="Name (Hindi)" value={nameHi} onChange={setNameHi} />
          <Input density="admin" label="Name (Sanskrit)" value={nameSa} onChange={setNameSa} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--sp-md)' }}>
          <Select
            density="admin"
            label="Category"
            value={words(category)}
            onChange={(v) => setCategory(CATEGORIES.find((c) => words(c) === v)!)}
            options={CATEGORIES.map(words)}
          />
          <Select
            density="admin"
            label="Kind"
            value={words(kind)}
            onChange={(v) => setKind(KINDS.find((k) => words(k) === v)!)}
            options={KINDS.map(words)}
          />
          <Input density="admin" label="Unit" value={unit} onChange={setUnit} placeholder="packet, piece, gram" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 'var(--sp-md)' }}>
          <Input density="admin" label="Usual quantity" value={quantity} onChange={setQuantity} inputMode="decimal" error={errors.quantity} />
          <Input density="admin" label="Unit cost" prefix="₹" value={cost} onChange={setCost} inputMode="decimal" error={errors.cost} />
          <Input density="admin" label="Cost as of" value={costAsOf} onChange={setCostAsOf} placeholder="2026-10-01" error={errors.costAsOf} />
          <Input density="admin" label="HSN code" value={hsn} onChange={setHsn} />
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-lg)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Switch label="Perishable" on={perishable} onChange={setPerishable} />
          <Switch label="Bought locally" on={local} onChange={setLocal} />
          <Input density="admin" label="Shelf life (days)" value={shelfLife} onChange={setShelfLife} inputMode="numeric" error={errors.shelfLife} />
        </div>
        <Input
          density="admin"
          label="Description"
          value={description}
          onChange={setDescription}
          multiline
          helper="What it is and what it is used for — never what it will bring about."
        />
        {mutation.isError && <Consequences tone="error" kicker={REFUSED} items={[errorMessage(mutation.error)]} />}
        <Actions>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!nameEn.trim() || !nameHi.trim() || !unit.trim() || (!row && !slug.trim()) || invalid || mutation.isPending}
            onClick={save}
          >
            {row ? 'Save' : 'Add item'}
          </ScopeAction>
          {row && (
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant={row.is_active ? 'destructive' : 'secondary'}
              disabled={mutation.isPending}
              onClick={() => toggle(row)}
            >
              {row.is_active ? 'Switch off' : 'Switch on'}
            </ScopeAction>
          )}
          <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={onDone}>
            {row ? 'Close' : 'Cancel'}
          </ScopeAction>
        </Actions>
      </div>
    </Card>
  );
}

function KitForm({ row, onDone }: { row: SamagriKitRow | null; onDone: () => void }) {
  const { scope, toast } = useConsole();
  const create = useCreateSamagriKit();
  const update = useUpdateSamagriKit();
  const mutation = row ? update : create;
  const [slug, setSlug] = useState('');
  const [puja, setPuja] = useState('');
  const [nameEn, setNameEn] = useState(row?.name_en ?? '');
  const [nameHi, setNameHi] = useState(row?.name_hi ?? '');
  const [region, setRegion] = useState<SamagriRegion>((row?.region_variant as SamagriRegion) ?? 'north');
  const [tier, setTier] = useState(row?.tier_hint ?? '');
  const [notes, setNotes] = useState(row?.notes ?? '');

  const pujaError = check(puja, (v) => /^\d{1,9}$/.test(v) && Number(v) >= 1, 'A puja id from the catalogue.');
  const fields = { name_en: nameEn.trim(), name_hi: nameHi.trim(), region_variant: region, tier_hint: blank(tier), notes: blank(notes) };
  const done = (message: string, tone: 'success' | 'info' | 'error' = 'success') => {
    toast({ tone, message });
    onDone();
  };
  const save = () =>
    row
      ? update.mutate({ id: row.id, patch: fields }, { onSuccess: () => done(`${row.slug} saved`) })
      : create.mutate(
          { ...fields, slug: slug.trim(), puja_id: puja.trim() ? Number(puja) : null },
          { onSuccess: () => done(`${slug.trim()} added`) },
        );
  const toggle = (target: SamagriKitRow) =>
    update.mutate(
      { id: target.id, patch: { is_active: !target.is_active } },
      {
        onSuccess: () =>
          done(`${target.slug} ${target.is_active ? 'switched off' : 'switched on'}`, target.is_active ? 'info' : 'success'),
      },
    );

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
      {row ? (
        <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
          The slug and puja are fixed once created — bookings cite the kit.
          {row.puja_id ? ` For puja #${row.puja_id}.` : ' Not tied to one puja.'}
        </span>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 200px', gap: 'var(--sp-md)' }}>
          <Input density="admin" label="Slug" value={slug} onChange={setSlug} placeholder="satyanarayan-north-standard" />
          <Input density="admin" label="Puja id" value={puja} onChange={setPuja} inputMode="numeric" helper="Blank for a general kit." error={pujaError} />
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-md)' }}>
        <Input density="admin" label="Name (English)" value={nameEn} onChange={setNameEn} />
        <Input density="admin" label="Name (Hindi)" value={nameHi} onChange={setNameHi} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-md)' }}>
        <Select
          density="admin"
          label="Region"
          value={words(region)}
          onChange={(v) => setRegion(REGIONS.find((r) => words(r) === v)!)}
          options={REGIONS.map(words)}
        />
        <Input density="admin" label="Tier" value={tier} onChange={setTier} placeholder="standard" />
      </div>
      <Input density="admin" label="Notes" value={notes} onChange={setNotes} multiline />
      {mutation.isError && <Consequences tone="error" kicker={REFUSED} items={[errorMessage(mutation.error)]} />}
      <Actions>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!nameEn.trim() || !nameHi.trim() || (!row && !slug.trim()) || Boolean(pujaError) || mutation.isPending}
          onClick={save}
        >
          {row ? 'Save kit' : 'Add kit'}
        </ScopeAction>
        {row && (
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant={row.is_active ? 'destructive' : 'secondary'}
            disabled={mutation.isPending}
            onClick={() => toggle(row)}
          >
            {row.is_active ? 'Switch off' : 'Switch on'}
          </ScopeAction>
        )}
        <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={onDone}>
          {row ? 'Close' : 'Cancel'}
        </ScopeAction>
      </Actions>
    </div>
  );
}

interface Line {
  /** The item's id, not its slug: a line can cite an item the list no longer returns. */
  item: number;
  quantity: string;
  optional: boolean;
  note: string;
}

function KitLines({ kit, items }: { kit: SamagriKitDetail; items: readonly SamagriItemRow[] }) {
  const { scope, toast } = useConsole();
  const replace = useReplaceKitLines();
  const slugOf = (id: number) => items.find((i) => i.id === id)?.slug ?? `#${id}`;
  const [lines, setLines] = useState<Line[]>(() =>
    kit.lines.map((l) => ({ item: l.samagri_item_id, quantity: l.quantity, optional: l.is_optional, note: l.vidhi_step_note ?? '' })),
  );
  const offered = items.filter((i) => i.is_active);
  const set = (index: number, patch: Partial<Line>) =>
    setLines((current) => current.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const add = () =>
    setLines((current) => [
      ...current,
      { item: (offered.find((o) => !current.some((l) => l.item === o.id)) ?? offered[0]!).id, quantity: '1', optional: false, note: '' },
    ]);
  const badQuantity = lines.some((l) => !QUANTITY.test(l.quantity.trim()) || Number(l.quantity) <= 0);
  const repeated = lines.some((l, i) => lines.findIndex((m) => m.item === l.item) !== i);
  const save = () =>
    replace.mutate(
      {
        id: kit.id,
        items: lines.map((l) => ({
          samagri_item_id: l.item,
          quantity: l.quantity.trim(),
          is_optional: l.optional,
          vidhi_step_note: blank(l.note),
        })),
      },
      { onSuccess: (saved) => toast({ tone: 'success', message: `${saved.slug}: ${saved.item_count} items saved` }) },
    );

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
      <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
        {kit.item_count} items · estimated {kit.est_cost_paise === null || kit.est_cost_paise === undefined ? 'cost unknown' : formatInr(kit.est_cost_paise)}
        {kit.cost_as_of && ` as of ${kit.cost_as_of}`} — worked out by the server from the saved lines.
      </span>
      {lines.length === 0 && (
        <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>No lines yet.</span>
      )}
      {lines.map((line, index) => (
        <div
          key={index}
          role="group"
          aria-label={`Line ${index + 1}`}
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) 110px auto minmax(0, 2fr) auto', gap: 'var(--sp-sm)', alignItems: 'end' }}
        >
          <Select
            density="admin"
            label="Item"
            value={slugOf(line.item)}
            onChange={(v) => set(index, { item: items.find((i) => i.slug === v)?.id ?? line.item })}
            options={[
              ...(offered.some((o) => o.id === line.item) ? [] : [slugOf(line.item)]),
              ...offered.map((o) => o.slug),
            ]}
          />
          <Input density="admin" label="Quantity" value={line.quantity} onChange={(v) => set(index, { quantity: v })} inputMode="decimal" />
          <Switch label="Optional" on={line.optional} onChange={(v) => set(index, { optional: v })} />
          <Input density="admin" label="Step note" value={line.note} onChange={(v) => set(index, { note: v })} />
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="secondary"
            onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
          >
            Remove
          </ScopeAction>
        </div>
      ))}
      {badQuantity && <Consequences tone="warn" items={['Every quantity is more than zero, with up to two decimal places.']} />}
      {repeated && <Consequences tone="warn" items={['An item appears twice. Put it on one line with the total quantity.']} />}
      {replace.isError && <Consequences tone="error" kicker={REFUSED} items={[errorMessage(replace.error)]} />}
      <Actions>
        <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" icon="plus" disabled={offered.length === 0} onClick={add}>
          Add line
        </ScopeAction>
        <ScopeAction scope={scope} need="catalogue_content" size="sm" disabled={badQuantity || repeated || replace.isPending} onClick={save}>
          Save lines
        </ScopeAction>
      </Actions>
    </div>
  );
}

function KitEditor({ row, items, onDone }: { row: SamagriKitRow; items: readonly SamagriItemRow[]; onDone: () => void }) {
  const detail = useSamagriKit(row.id);
  return (
    <Card variant="flat" padding="var(--sp-lg)">
      <div style={{ display: 'grid', gap: 'var(--sp-lg)' }}>
        <strong>{row.slug}</strong>
        <KitForm row={row} onDone={onDone} />
        {detail.isPending && <LoadingState message="Reading the kit's lines…" />}
        {detail.isError && <ErrorState title="Could not read the kit's lines" cause={errorMessage(detail.error)} />}
        {detail.isSuccess && <KitLines key={detail.dataUpdatedAt} kit={detail.data} items={items} />}
      </div>
    </Card>
  );
}

const status = (active: boolean) => <StatusChip tone={active ? 'success' : 'neutral'} label={active ? 'Offered' : 'Off'} />;
const cost = (paise: number | null | undefined) =>
  paise === null || paise === undefined ? <span style={{ color: 'var(--c-ink3)' }}>—</span> : <Money paise={paise} />;

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS: readonly string[] = ['items', 'kits'];

export function Samagri() {
  const { scope } = useConsole();
  const [tab, setTab] = useUrlTab('samagri', TAB_KEYS, 'items');
  const [q, setQ] = useUrlSearch('samagri');
  const [open, setOpen] = useState<number | 'new' | null>(null);
  const items = useSamagriItems();
  const kits = useSamagriKits();
  const onItems = tab === 'items';
  const list = onItems ? items : kits;
  const allItems = items.data?.items ?? [];
  const allKits = kits.data?.items ?? [];
  const needle = q.trim().toLowerCase();
  const matches = (r: { slug: string; name_en: string; name_hi: string }) =>
    !needle || `${r.slug} ${r.name_en} ${r.name_hi}`.toLowerCase().includes(needle);
  const close = () => setOpen(null);
  const openItem = onItems && typeof open === 'number' ? allItems.find((r) => r.id === open) : undefined;
  const openKit = !onItems && typeof open === 'number' ? allKits.find((r) => r.id === open) : undefined;
  const total = onItems ? allItems.length : allKits.length;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-sunk)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          color: 'var(--c-ink2)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name="package" size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          The items a puja uses and the kits they are packed in. A kit&rsquo;s count and estimated
          cost are worked out from its lines; items and kits are switched off, never deleted.
        </span>
        <ScopeAction scope={scope} need="catalogue_content" size="sm" icon="plus" onClick={() => setOpen('new')}>
          {onItems ? 'New item' : 'New kit'}
        </ScopeAction>
      </div>

      <FilterBar
        q={q}
        onQ={setQ}
        placeholder="Slug or name — press /"
        tab={tab}
        onTab={(next) => {
          setTab(next);
          setOpen(null);
        }}
        tabs={[
          { key: 'items', label: 'Items' },
          { key: 'kits', label: 'Kits' },
        ]}
      />

      {open === 'new' && (onItems ? <ItemForm row={null} onDone={close} /> : (
        <Card variant="flat" padding="var(--sp-lg)">
          <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
            <strong>New samagri kit</strong>
            <KitForm row={null} onDone={close} />
          </div>
        </Card>
      ))}
      {openItem && <ItemForm key={openItem.id} row={openItem} onDone={close} />}
      {openKit && <KitEditor key={openKit.id} row={openKit} items={allItems} onDone={close} />}

      {list.isPending && <LoadingState message={onItems ? 'Reading the samagri items…' : 'Reading the samagri kits…'} />}
      {list.isError && (
        <ErrorState title={onItems ? 'Could not read the samagri items' : 'Could not read the samagri kits'} cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && total === 0 && (
        <Card variant="flat" padding="var(--sp-xxl)">
          <EmptyState
            icon="package"
            title={onItems ? 'No samagri items yet' : 'No samagri kits yet'}
            body="The samagri seed adds the standard set. An empty table means it has not run."
          />
        </Card>
      )}

      {onItems && items.isSuccess && total > 0 && (
        <DataTable
          density="comfortable"
          onRowClick={(row) => setOpen(Number(row.id))}
          footer={<span>{allItems.filter(matches).length} of {total} items</span>}
          columns={[
            { key: 'slug', label: 'Slug', mono: true },
            { key: 'name', label: 'Name' },
            { key: 'category', label: 'Category' },
            { key: 'unit', label: 'Unit' },
            { key: 'cost', label: 'Unit cost', align: 'right' },
            { key: 'status', label: 'Status' },
          ]}
          rows={allItems.filter(matches).map((r) => ({
            id: r.id,
            slug: r.slug,
            name: `${r.name_en} · ${r.name_hi}`,
            category: words(r.category),
            unit: r.unit,
            cost: cost(r.unit_cost_paise),
            status: status(r.is_active),
          }))}
        />
      )}

      {!onItems && kits.isSuccess && total > 0 && (
        <DataTable
          density="comfortable"
          onRowClick={(row) => setOpen(Number(row.id))}
          footer={<span>{allKits.filter(matches).length} of {total} kits</span>}
          columns={[
            { key: 'slug', label: 'Slug', mono: true },
            { key: 'name', label: 'Name' },
            { key: 'region', label: 'Region' },
            { key: 'count', label: 'Items', align: 'right' },
            { key: 'cost', label: 'Est. cost', align: 'right' },
            { key: 'status', label: 'Status' },
          ]}
          rows={allKits.filter(matches).map((r) => ({
            id: r.id,
            slug: r.slug,
            name: `${r.name_en} · ${r.name_hi}`,
            region: words(r.region_variant),
            count: r.item_count,
            cost: cost(r.est_cost_paise),
            status: status(r.is_active),
          }))}
        />
      )}
    </>
  );
}
